import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import {
  loginInfoQueryCreator,
  userLocationQueryCreator,
  userQueryCreator,
  workspaceQueryCreator,
} from '#query';
import { isValidZipCode } from '#utils';

export const useLocationState = ({
  isWorkspaceEnabled,
  onLocationPrefilled,
  initialLocation,
}: {
  isWorkspaceEnabled: boolean;
  onLocationPrefilled?: (address: {
    country: string;
    postal_code: string;
  }) => Promise<void>;
  initialLocation?: { country: string; zip: string };
}) => {
  const queryClient = useQueryClient();
  const { data: loginData } = useQuery(loginInfoQueryCreator());
  const isLoggedIn = loginData?.['logged-in'];

  const [locationState, setLocationState] = useState<{
    country: string;
    zip: string;
    state?: string;
  }>(initialLocation ?? { country: 'US', zip: '' });

  const locationWasPrefilled = useRef(!!initialLocation);
  const initialLocationWasPrefilled = useRef(false);

  useEffect(() => {
    if (initialLocation && !initialLocationWasPrefilled.current) {
      onLocationPrefilled?.({
        country: initialLocation.country,
        postal_code: initialLocation.zip,
      });
      initialLocationWasPrefilled.current = true;
    }
  }, [initialLocation]);

  useEffect(() => {
    const handleLocationPrefilled = async (address: {
      country: string;
      postal_code: string;
      state?: string;
    }) => {
      setLocationState({
        country: address.country || 'US',
        zip: address.postal_code || '',
        state: address.state || '',
      });
      onLocationPrefilled?.(address);
      locationWasPrefilled.current = true;
    };
    const getAndSetUserLocation = async () => {
      const userLocation = await queryClient.fetchQuery(
        userLocationQueryCreator(),
      );
      if (userLocation?.country_code || userLocation?.zip_code) {
        const isUSorCA =
          userLocation.country_code === 'US' ||
          userLocation.country_code === 'CA';
        // only prefill from ipinfo if the country is US or CA
        const postalCode = isUSorCA ? userLocation.zip_code : '';
        handleLocationPrefilled({
          country: userLocation.country_code || 'US',
          postal_code: postalCode,
        });
      }
    };

    // Helper to extract location from settings object
    const extractLocationFromSettings = (
      settings:
        | {
            country_iso_code?: string;
            zip_code?: string;
          }
        | null
        | undefined,
    ) => {
      if (!settings?.country_iso_code && !settings?.zip_code) return null;
      return {
        country: settings.country_iso_code || 'US',
        postal_code: settings.zip_code || '',
      };
    };

    const getLocationPrefill = async () => {
      if (locationWasPrefilled.current) return;

      let locationData = null;

      if (isLoggedIn) {
        if (isWorkspaceEnabled) {
          const workspace = await queryClient.fetchQuery(
            workspaceQueryCreator(),
          );
          locationData = extractLocationFromSettings(
            workspace?.workspace?.settings,
          );
        } else {
          const userData = await queryClient.fetchQuery(userQueryCreator());
          locationData = extractLocationFromSettings(userData?.settings);
        }
      }

      // Use prefilled data or fallback to user location
      locationData
        ? handleLocationPrefilled(locationData)
        : getAndSetUserLocation();
    };
    if (!initialLocation) {
      getLocationPrefill();
    }
  }, [queryClient, isWorkspaceEnabled, onLocationPrefilled, initialLocation]);

  const handleLocationChange = (country: string, zip: string) => {
    setLocationState({ country, zip });
  };

  const zipIsValid = isValidZipCode(locationState.zip, locationState.country);

  return {
    locationState,
    handleLocationChange,
    zipIsValid,
    isUserLocationLoading: false,
  };
};
